import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const CONTRACTOR_PORTFOLIO_REQUIREMENTS = [
  'Project title',
  'At least one completed project photo'
];

const parseImages = (images) => {
  if (!images) return [];
  if (Array.isArray(images)) return images;
  if (typeof images === 'string') {
    try {
      const parsed = JSON.parse(images);
      return Array.isArray(parsed) ? parsed : [images];
    } catch (_) {
      return images.trim() ? [images] : [];
    }
  }
  return [];
};

const isVerifiablePortfolioItem = (item) => {
  const afterImages = parseImages(item.after_images || item.afterImages);
  const beforeImages = parseImages(item.before_images || item.beforeImages);
  // Verifiable = titled project + at least one photo (new modal stores after_images,
  // older editor projects may use before_images). Description/type/date/client phone
  // are no longer required here.
  return Boolean(
    item.is_public !== false &&
    (item.project_title || item.projectTitle || '').trim() &&
    (afterImages.length + beforeImages.length) > 0
  );
};

/**
 * Hook to check if a professional's profile is complete enough to perform gated actions.
 *
 * Profile completeness gates apply to all professional roles:
 * - Contractors: bid, message, accept hires
 * - Verifiers: apply, message, accept verifications
 * - Quality Inspectors: accept inspections, message
 * - Project Managers: apply, message, accept assignments
 * - Merchants: list products, message, accept orders
 *
 * Clients are never gated - they can immediately create projects.
 */
const useProfileGate = () => {
  const { user } = useAuth();
  // Assume complete until the check answers: a contractor landing from an
  // email link who taps Reply before the two requests finish used to be sent
  // to the onboarding wizard. A wrong 'allow' only defers the gate to the server.
  const [profileComplete, setProfileComplete] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGateDialog, setShowGateDialog] = useState(false);
  const [gateAction, setGateAction] = useState('');
  const [hasVerifiablePortfolio, setHasVerifiablePortfolio] = useState(false);

  // Professional roles that require profile completion
  const professionalRoles = ['contractor', 'verifier', 'professional_verifier', 'quality_inspector', 'project_manager', 'merchant'];
  const isProfessional = user?.role && professionalRoles.includes(user.role);
  const isContractor = user?.role === 'contractor';

  useEffect(() => {
    if (isProfessional) {
      checkProfileCompleteness();
    } else {
      // Clients and non-professionals are never gated
      setProfileComplete(true);
      setLoading(false);
    }
  }, [user, isProfessional]);

  const checkProfileCompleteness = useCallback(async () => {
    try {
      setLoading(true);

      const applyContractorPortfolioRequirement = async (baseComplete, baseData) => {
        if (user?.role !== 'contractor') {
          setHasVerifiablePortfolio(false);
          setProfileComplete(baseComplete);
          setProfileData(baseData);
          return;
        }

        try {
          const portfolioResponse = await api.get('/contractor/portfolio');
          const portfolioItems = portfolioResponse.data?.data?.portfolio || [];
          const hasPortfolio = portfolioItems.some(isVerifiablePortfolioItem);
          const baseMissing = baseData?.missingItems || baseData?.missingRequirements || [];
          const missingRequirements = hasPortfolio
            ? baseMissing
            : Array.from(new Set([...baseMissing, ...CONTRACTOR_PORTFOLIO_REQUIREMENTS]));
          setHasVerifiablePortfolio(hasPortfolio);
          setProfileComplete(baseComplete && hasPortfolio);
          setProfileData({
            ...baseData,
            baseProfileComplete: baseComplete,
            portfolioRequired: true,
            hasVerifiablePortfolio: hasPortfolio,
            missingRequirements
          });
        } catch (err) {
          // Network/session blip: unknown is not "incomplete". Keep the base
          // answer and let the server enforce the portfolio rule on bid.
          console.warn('Contractor portfolio requirement check failed');
          setHasVerifiablePortfolio(false);
          setProfileComplete(baseComplete);
          setProfileData({
            ...baseData,
            baseProfileComplete: baseComplete,
            portfolioRequired: true,
            hasVerifiablePortfolio: false,
            missingRequirements: baseData?.missingItems || baseData?.missingRequirements || []
          });
        }
      };

      // Try unified completeness endpoint first (works for all professional roles)
      try {
        const response = await api.get('/onboarding/profile/completeness');
        if (response.data.success) {
          const completeness = response.data;
          await applyContractorPortfolioRequirement(completeness.isComplete, completeness);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn('Unified completeness endpoint failed, trying role-specific endpoint');
      }

      // Fallback: try role-specific endpoints
      const role = user?.role;
      if (role === 'verifier' || role === 'professional_verifier') {
        try {
          const response = await api.get('/verifiers/profile/me/completeness');
          if (response.data.success) {
            setProfileComplete(response.data.data.isComplete);
            setProfileData(response.data.data);
            setHasVerifiablePortfolio(false);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.warn('Verifier completeness check failed');
        }
      } else if (role === 'quality_inspector') {
        try {
          const response = await api.get('/inspectors/profile/me/completeness');
          if (response.data.success) {
            setProfileComplete(response.data.data.isComplete);
            setProfileData(response.data.data);
            setHasVerifiablePortfolio(false);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.warn('Inspector completeness check failed');
        }
      } else if (role === 'contractor') {
        try {
          const response = await api.get('/onboarding/status');
          if (response.data.success) {
            const status = response.data.data;
            const isComplete = status.currentPhase >= 2 && status.completed?.basic_profile;
            await applyContractorPortfolioRequirement(isComplete, status);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.warn('Contractor onboarding status check failed');
        }
      }

      // Final fallback: check user profile fields directly from auth context
      const profile = user?.profile;
      if (profile) {
        const hasBio = !!(profile.bio && profile.bio.trim().length > 0);
        const hasCity = !!(profile.city && profile.city.trim().length > 0);
        const isComplete = hasBio && hasCity;
        await applyContractorPortfolioRequirement(isComplete, profile);
      } else {
        // No profile data reachable: unknown, not incomplete. The server
        // still gates bids and messages on its own checks.
        setProfileComplete(true);
        setHasVerifiablePortfolio(false);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Check if an action is allowed. If not, show the gate dialog.
   * @param {string} action - The action being attempted ('bid', 'message', 'accept', 'apply')
   * @returns {boolean} - Whether the action is allowed
   */
  const checkAction = useCallback((action) => {
    if (!isProfessional || profileComplete || loading) {
      return true; // Allow action (non-professionals, complete profiles, or check still running)
    }

    // Block the action and show dialog
    setGateAction(action);
    setShowGateDialog(true);
    return false;
  }, [isProfessional, profileComplete, loading]);

  const closeGateDialog = useCallback(() => {
    setShowGateDialog(false);
    setGateAction('');
  }, []);

  return {
    profileComplete,
    profileData,
    hasVerifiablePortfolio,
    portfolioRequired: isContractor,
    isContractor,
    portfolioRequirements: CONTRACTOR_PORTFOLIO_REQUIREMENTS,
    loading,
    isProfessional,
    userRole: user?.role,
    checkAction,
    showGateDialog,
    gateAction,
    closeGateDialog,
    refreshProfile: checkProfileCompleteness
  };
};

export default useProfileGate;
